import React, { Component } from 'react'
import styles from './profile.module.scss'
import { connect } from 'react-redux'
import { Link } from 'react-router-dom'
import {
  Button,
  Card,
  CardContent,
  Typography,
  CardActionArea,
  CardActions,
  CircularProgress,
  Divider,
  Switch
} from '@material-ui/core'
import Autocomplete from '@material-ui/lab/Autocomplete'
import TextField from '@material-ui/core/TextField'
import axios from 'axios'
import DeleteDialog from '../Common/DeleteDialog'

const DEFAULT_NOTIFICATION_PREFS = {
  pushEnabled: true,
  smsEnabled: false,
  emailEnabled: true,
  level: 'all_updates',
  herbicide: true,
  irrigation: true,
  issues: true,
  workTicketCompletion: true,
  dailySummary: false,
  weeklySummary: true
}

const NOTIFICATION_LEVELS = [
  { value: 'urgent_only', label: 'Urgent only' },
  { value: 'key_updates', label: 'Key updates' },
  { value: 'all_updates', label: 'All updates' }
]

const NOTIFICATION_CHANNELS = [
  { key: 'pushEnabled', label: 'Push notifications', help: 'Device alerts for matching operational updates.' },
  { key: 'smsEnabled', label: 'SMS alerts', help: 'Text-message alerts for qualifying urgent updates.' },
  { key: 'emailEnabled', label: 'Email', help: 'Email alerts and summary notifications.' }
]

const NOTIFICATION_CATEGORIES = [
  { key: 'herbicide', label: 'Herbicide', help: 'Queue changes, field activity, subcontractor updates, and application status.' },
  { key: 'irrigation', label: 'Irrigation', help: 'Irrigation inspections, repair updates, and follow-up items.' },
  { key: 'issues', label: 'Issues', help: 'Customer issues, property line issues, complaints, and escalations.' },
  { key: 'workTicketCompletion', label: 'Work Ticket Completion', help: 'Completed work tickets and closeout activity.' }
]

class Profile extends Component {
  constructor(props) {
    super(props)
  // console.log('Profile PRops', props)
    this.state = {
      open: false,
      showDeleteDialog: false,
      calculationConfigs: [],
      preferredConfig: null,
      loadingConfigs: false,
      notificationPrefs: DEFAULT_NOTIFICATION_PREFS,
      loadingNotifications: false,
      savingNotifications: false,
      notificationDirty: false,
      notificationError: '',
      notificationHasPhone: false,
      notificationPhoneInput: ''
    }
  }

  componentDidMount() {
    this.loadCalculationConfigs()
    this.loadNotificationPreferences()
  }

  loadCalculationConfigs = async () => {
    this.setState({ loadingConfigs: true })
    try {
      // Load all configs
      const configsResponse = await axios.get(`${this.props.url}/api/calculation-configs`)
      const configs = configsResponse.data.configs || []

      // Load user preference
      const preferenceResponse = await axios.get(`${this.props.url}/api/calculation-configs/user/preference`)
      const preferredConfigId = preferenceResponse.data.configId

      const preferredConfig = configs.find(c => c._id === preferredConfigId) || null

      this.setState({
        calculationConfigs: configs,
        preferredConfig: preferredConfig,
        loadingConfigs: false
      })
    } catch (error) {
      console.error('Error loading calculation configs:', error)
      this.setState({ loadingConfigs: false })
    }
  }

  handleUpdatePreferredConfig = async (event, config) => {
    this.setState({ loadingConfigs: true })
    try {
      await axios.put(`${this.props.url}/api/calculation-configs/user/preference`, {
        configId: config?._id || null
      })

      this.setState({
        preferredConfig: config,
        loadingConfigs: false
      })

      this.props.notification({
        type: 'success',
        title: 'Preference Updated',
        message: 'Your preferred calculation configuration has been saved!'
      })
    } catch (error) {
      console.error('Error updating preferred config:', error)
      this.setState({ loadingConfigs: false })
      this.props.notification({
        type: 'danger',
        title: 'Update Failed',
        message: 'Failed to update your preferred calculation configuration'
      })
    }
  }

  loadNotificationPreferences = async () => {
    this.setState({ loadingNotifications: true, notificationError: '' })
    try {
      const response = await axios.get(`${this.props.url}/api/user/notification-prefs`)
      this.setState({
        notificationPrefs: { ...DEFAULT_NOTIFICATION_PREFS, ...(response.data?.prefs || {}) },
        notificationHasPhone: response.data?.hasPhone === true,
        notificationPhoneInput: response.data?.phoneOnFile || '',
        notificationDirty: false,
        loadingNotifications: false
      })
    } catch (error) {
      console.error('Error loading notification preferences:', error)
      this.setState({
        loadingNotifications: false,
        notificationError: error?.response?.data?.error || 'Unable to load notification preferences.'
      })
    }
  }

  updateNotificationPref = (key, value) => {
    this.setState(prev => ({
      notificationPrefs: {
        ...prev.notificationPrefs,
        [key]: value
      },
      notificationDirty: true
    }))
  }

  updateNotificationPhone = (value) => {
    this.setState({
      notificationPhoneInput: value,
      notificationDirty: true
    })
  }

  saveNotificationPreferences = async () => {
    this.setState({ savingNotifications: true })
    try {
      const payload = { prefs: this.state.notificationPrefs }
      if (this.state.notificationPhoneInput.trim()) {
        payload.phone = this.state.notificationPhoneInput.trim()
      }
      const response = await axios.put(`${this.props.url}/api/user/notification-prefs`, payload)
      this.setState({
        notificationPrefs: { ...DEFAULT_NOTIFICATION_PREFS, ...(response.data?.prefs || {}) },
        notificationHasPhone: response.data?.hasPhone === true,
        notificationPhoneInput: response.data?.phoneOnFile || '',
        notificationDirty: false,
        savingNotifications: false
      })
      this.props.notification({
        type: 'success',
        title: 'Preferences saved',
        message: 'Your notification preferences have been updated.'
      })
    } catch (error) {
      console.error('Error saving notification preferences:', error)
      this.setState({ savingNotifications: false })
      this.props.notification({
        type: 'danger',
        title: 'Save failed',
        message: error?.response?.data?.error || 'Unable to save notification preferences.'
      })
    }
  }

  renderNotificationRow = (item) => {
    const checked = Boolean(this.state.notificationPrefs[item.key])
    return (
      <div key={item.key} style={{
        display: 'flex',
        alignItems: 'center',
        gap: 16,
        padding: '12px 0',
        borderBottom: '1px solid #eeeeee'
      }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <Typography style={{ fontSize: 14, fontWeight: 600 }}>{item.label}</Typography>
          <Typography color="textSecondary" style={{ fontSize: 12 }}>{item.help}</Typography>
        </div>
        <Switch
          checked={checked}
          onChange={(event) => this.updateNotificationPref(item.key, event.target.checked)}
          color="primary"
        />
      </div>
    )
  }

  renderNotificationPreferences = () => {
    const { notificationPrefs, loadingNotifications, savingNotifications, notificationDirty, notificationError } = this.state

    if (loadingNotifications) {
      return (
        <div style={{ padding: '12px 0' }}>
          <CircularProgress size={24} />
        </div>
      )
    }

    if (notificationError) {
      return (
        <div style={{ padding: '12px 0' }}>
          <Typography color="error" gutterBottom>{notificationError}</Typography>
          <Button size="small" variant="outlined" onClick={this.loadNotificationPreferences}>Retry</Button>
        </div>
      )
    }

    return (
      <div>
        <Typography color="textSecondary" gutterBottom>
          Choose how you want to receive internal operational alerts.
        </Typography>

        <Typography variant="subtitle2" style={{ marginTop: 12, fontWeight: 700 }}>
          Delivery Channels
        </Typography>
        {NOTIFICATION_CHANNELS.map(this.renderNotificationRow)}

        {notificationPrefs.smsEnabled && (
          <div style={{
            padding: '12px 0',
            borderBottom: '1px solid #eeeeee',
            background: this.state.notificationHasPhone ? 'transparent' : '#fff8e1'
          }}>
            <Typography style={{ fontSize: 14, fontWeight: 600 }}>
              {this.state.notificationHasPhone ? 'SMS phone number' : 'Phone number required'}
            </Typography>
            <Typography color="textSecondary" style={{ fontSize: 12, marginBottom: 8 }}>
              SMS alerts will be sent to this number.
            </Typography>
            <TextField
              variant="outlined"
              size="small"
              placeholder="(602) 555-1234"
              value={this.state.notificationPhoneInput}
              onChange={(event) => this.updateNotificationPhone(event.target.value)}
              style={{ maxWidth: 240 }}
            />
          </div>
        )}

        <Typography variant="subtitle2" style={{ marginTop: 16, fontWeight: 700 }}>
          Alert Categories
        </Typography>
        {NOTIFICATION_CATEGORIES.map(this.renderNotificationRow)}

        <Typography variant="subtitle2" style={{ marginTop: 16, fontWeight: 700 }}>
          Frequency
        </Typography>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 16,
          padding: '12px 0',
          borderBottom: '1px solid #eeeeee',
          flexWrap: 'wrap'
        }}>
          <div>
            <Typography style={{ fontSize: 14, fontWeight: 600 }}>Notification level</Typography>
            <Typography color="textSecondary" style={{ fontSize: 12 }}>How much we send when an enabled category has activity.</Typography>
          </div>
          <div style={{ display: 'inline-flex', border: '1px solid #d0d0d0', borderRadius: 6, overflow: 'hidden' }}>
            {NOTIFICATION_LEVELS.map(option => (
              <button
                key={option.value}
                type="button"
                onClick={() => this.updateNotificationPref('level', option.value)}
                style={{
                  border: 0,
                  borderRight: option.value === NOTIFICATION_LEVELS[NOTIFICATION_LEVELS.length - 1].value ? 0 : '1px solid #d0d0d0',
                  background: notificationPrefs.level === option.value ? '#1976d2' : '#fff',
                  color: notificationPrefs.level === option.value ? '#fff' : '#333',
                  padding: '7px 10px',
                  fontSize: 12,
                  fontWeight: notificationPrefs.level === option.value ? 700 : 500,
                  cursor: 'pointer'
                }}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>

        {[
          { key: 'dailySummary', label: 'Daily digest', help: 'A morning summary of the prior day.' },
          { key: 'weeklySummary', label: 'Weekly digest', help: 'A Monday summary of last week.' }
        ].map(this.renderNotificationRow)}

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, paddingTop: 16 }}>
          <Typography color="textSecondary" style={{ fontSize: 12 }}>
            {notificationDirty ? 'Unsaved changes' : 'All changes saved'}
          </Typography>
          <div style={{ display: 'flex', gap: 8 }}>
            <Button
              variant="outlined"
              onClick={this.loadNotificationPreferences}
              disabled={savingNotifications}
            >
              Reload
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={this.saveNotificationPreferences}
              disabled={savingNotifications || !notificationDirty}
            >
              {savingNotifications ? 'Saving...' : 'Save Preferences'}
            </Button>
          </div>
        </div>
      </div>
    )
  }

  removeUser = async () => {
    const { id, email } = this.props.user
    const userdata = {
      data: {
        id,
        email
      }
    }
    this.setState({
      loading: true
    })
    const response = await axios.delete(`${this.props.url}/api/user/profileremove`, userdata)
    this.setState({
      loading: false
    })
    if (response.data.success) {
      this.props.notification({
        type: 'success',
        title: 'Success!',
        message: 'Profile deleted!'
      })
      this.props.history.push('/logout')
    } else {
      this.props.notification({
        type: 'danger',
        title: 'Delete failed!',
        message: 'Try again later!'
      })
    }
  }

  removeDialogClick = () => {
    this.setState({
      open: this.state.open ? false : true
    })
  }

   openDeleteDialog = () => {
  // console.log('open dleete ')
    this.setState({
      showDeleteDialog: true
    })
  }

  updateFromDeleteDialog = (deleteIt) => {
    this.setState({
      showDeleteDialog: false
    })
  // console.log('To Delete or Not To Delete?', deleteIt)
    // if (deleteIt) {
    //   window.alert('DELETE IT!')
    // } else {
    //   window.alert('Save it!')
    // }
  }
  render() {
    return (
      <div className={ styles.profile }>
        <DeleteDialog
          title="Delete Your Profile?"
          verificationText="Are you sure?"
          confirmationOfIntentText="This action is irreversible, you will lose access to this site and all of your data!"
          confirmationButton="DELETE"
          denyText="NO"
          open={this.state.showDeleteDialog}
          closeModal={this.updateFromDeleteDialog}
        />
        <Card className={ styles.card }>
          <CardContent>
            <Typography variant="h5" component="h2" gutterBottom>
              Profile
            </Typography>
            <Typography color="textSecondary">
              Username
            </Typography>
            <Typography component="p" gutterBottom>
              { this.props.user.username }
            </Typography>
            <Typography color="textSecondary">
              Name
            </Typography>
            <Typography component="p" gutterBottom>
              { this.props.user.name }
            </Typography>
            <Typography color="textSecondary">
              Email
            </Typography>
            <Typography component="p" gutterBottom>
              { this.props.user.email }
            </Typography>
            <Typography color="textSecondary">
              Telephone Number (SMS Alerts)
            </Typography>
            <Typography component="p" gutterBottom>
              { this.props.user.telephone ? this.props.user.telephone : <span style={{ color: 'red', fontSize: '1.2em' }}>Please Provide a Telephone #</span> }
            </Typography>

            <Divider style={{ margin: '20px 0' }} />

            <Typography variant="h6" gutterBottom>
              Calculation Preferences
            </Typography>
            <Typography color="textSecondary" gutterBottom>
              Preferred Calculation Configuration
            </Typography>
            {this.state.loadingConfigs ? (
              <CircularProgress size={24} />
            ) : (
              <Autocomplete
                id="PreferredCalculationConfig"
                options={this.state.calculationConfigs}
                getOptionLabel={(option) => option.name || ''}
                value={this.state.preferredConfig}
                onChange={this.handleUpdatePreferredConfig}
                style={{ marginTop: 10, marginBottom: 20 }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    label="Preferred Calculation Config"
                    helperText="This configuration will be used by default for all your estimates"
                    fullWidth
                  />
                )}
              />
            )}

            <Divider style={{ margin: '20px 0' }} />

            <Typography variant="h6" gutterBottom>
              Notification Preferences
            </Typography>
            {this.renderNotificationPreferences()}
          </CardContent>
          <CardActionArea>
            <CardActions>
              <Button
                className={ styles.button }
                variant="contained"
                color="neutral"
              >
                <Link className={ styles.link } to="/profile/modify">Modify</Link>
              </Button>
            </CardActions>
          </CardActionArea>
        </Card>
      </div>
    )
  }
}

const mapStateToProps = (state) => {
  return {
    url: state.url,
    user: state.user,
    notification: state.notification
  }
}

export default connect(mapStateToProps)(Profile)
